import React, { useState, useEffect, useRef } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useWordsData } from '../../lib/useWordsData';
import { useSpeakAnswer } from '../../lib/useSpeakAnswer';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './Generalization.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

// Карта картинок
const IMAGE_MAP = {
  'мяч': 'ball', 'кукла': 'doll', 'машинка': 'small_car', 'пирамидка': 'pyramid',
  'рубашка': 'shirt', 'платье': 'dress', 'шорты': 'shorts', 'юбка': 'skirt',
  'тапки': 'slippers', 'туфли': 'shoes', 'ботинки': 'boots', 'кеды': 'sneakers',
  'стул': 'chair', 'стол': 'table', 'шкаф': 'wardrobe', 'кресло': 'armchair',
  'огурец': 'cucumber', 'помидор': 'tomato', 'морковь': 'carrot', 'лук': 'onion',
  'яблоко': 'apple', 'персик': 'peach', 'апельсин': 'orange', 'груша': 'pear',
  'воробей': 'sparrow', 'голубь': 'pigeon', 'сова': 'owl', 'сорока': 'magpie',
  'клубника': 'strawberry', 'смородина': 'currant', 'черника': 'blueberry', 'малина': 'raspberry',
  'стрекоза': 'dragonfly', 'божья коровка': 'lady_bug', 'бабочка': 'butterfly', 'пчела': 'bee',
  'кошка': 'cat', 'собака': 'dog', 'корова': 'cow', 'коза': 'goat',
  'самолет': 'airplane', 'автобус': 'bus', 'такси': 'taxi', 'поезд': 'train'
};

const TASKS_BY_AGE = {
  '4': ['gen_toys', 'gen_clothes', 'gen_shoes'],
  '5': ['gen_furniture', 'gen_veggies', 'gen_fruits', 'gen_birds'],
  '6': ['gen_berries', 'gen_insects', 'gen_pets', 'gen_transport'],
  '7-9': ['gen_berries', 'gen_insects', 'gen_pets', 'gen_transport'], '10-12': [], '13-15': [], '16+': []
};

const Generalization = ({ ageGroup, onBack, onComplete }) => {
  const { data: wordsData, loading: wordsLoading } = useWordsData();
  const { start: startSpeakRecognition, stop: stopSpeakRecognition } = useSpeakAnswer('Generalization');
  const { play: playInstruction } = useInstructionAudio();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recognizerReady, setRecognizerReady] = useState(false);

  const hasCompletedRef = useRef(false);

  const taskKeys = TASKS_BY_AGE[ageGroup] || [];
  const totalQuestions = taskKeys.length;
  const currentTask = wordsData && taskKeys[currentQuestionIndex] ? wordsData.generalizations[taskKeys[currentQuestionIndex]] : null;

  const getImageSrc = (id) => `${S3_BASE_URL}/${IMAGE_MAP[id] || 'placeholder'}.png`;

  // --- ОЗВУЧКА ИНСТРУКЦИИ (пред-озвученный клип из БД) + РАСПОЗНАВАНИЕ РЕЧИ ---
  // Запускаются одновременно — распознаватель раньше стартовал только после
  // озвучки и не успевал подключить микрофон к моменту, когда ребёнок уже
  // начинал отвечать.
  const speakInstruction = () => {
    if (!currentTask || isSpeaking) return;
    setIsSpeaking(true);
    setRecognizerReady(false);

    startSpeakRecognition(
      [{ key: 'correct', text: currentTask.answer }],
      {
        onListening: () => setRecognizerReady(true),
        onResult: ({ matchedKey }) => {
          setIsListening(false);
          handleAnswer(matchedKey === 'correct');
        },
      }
    );

    playInstruction('Назови одним словом', () => {
      setIsSpeaking(false);
    });
  };

  // isListening (🎧) reflects the recognizer actually being ready, not just
  // "the announcement finished" — it may still be warming up on a cold
  // start even though it began connecting back when speakInstruction ran.
  useEffect(() => {
    setIsListening(!isSpeaking && recognizerReady);
  }, [isSpeaking, recognizerReady]);

  const handleAnswer = (isCorrect) => {
    setIsProcessing(true);
    setAnswers(prev => [...prev, isCorrect]);
    stopSpeakRecognition();

    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        goToNext();
      } else {
        setShowResult(true);
      }
    }, 2000); 
  };

  const goToNext = () => {
    setCurrentQuestionIndex(prev => prev + 1);
    setIsProcessing(false);
  };

  // --- ЗАПУСК ПРИ СТАРТЕ ---
  useEffect(() => {
    if (currentTask && !showResult && !isSpeaking && !isListening && !isProcessing) {
      const timer = setTimeout(speakInstruction, 600);
      return () => clearTimeout(timer);
    }
    // currentTask must be a dependency: it starts null until useWordsData's
    // async fetch resolves, which happens after this effect's first run —
    // without it, the effect never re-fires once the data actually arrives
    // and the instruction never plays.
  }, [currentQuestionIndex, showResult, currentTask]);


    // === ПЕРЕДАЧА РЕЗУЛЬТАТА В ОТЧЕТ ===
  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length;
        const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
        
        // ВНИМАНИЕ: Здесь подставляем нужный testId и правильный проп (onComplete или onNextModule)
        if (onComplete) onComplete({ testId: 'Generalization', score: score });
        else onBack();
      }, 4000); // 4 секунды задержки, чтобы показать экран с результатом
      return () => clearTimeout(timer);
    }
  }, [showResult]);

   // --- Автопереход к следующему модулю ---
  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        // ЗАЩИТА ОТ ДВОЙНОГО СРАБАТЫВАНИЯ:
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true;
          if (onComplete) {
            onComplete();
          } else {
            onBack();
          }
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (wordsLoading || !currentTask) return (
    <ModuleTemplate
      progress={answers}
      totalQuestions={totalQuestions}
      forceStop={true}
      onComplete={onComplete}
    >
      <div className="loading-text">Загрузка...</div>
    </ModuleTemplate>
  );

   return (
    <ModuleTemplate
      progress={answers}
      totalQuestions={totalQuestions}
      forceStop={true}
      onComplete={onComplete}
      extraContent="Назови одним словом"
    >
      <div className="gen-container">
        <div className="gen-main-row">
          
          {/* СЕТКА 2х2 СЛЕВА */}
          <div className="gen-grid">
            {currentTask.items.map(item => (
              <div key={item.id} className="gen-card">
                <img src={getImageSrc(item.id)} alt={item.text} className="gen-img" onError={(e)=>e.target.src='/placeholder.png'} />
              </div>
            ))}
          </div>

          {/* ИНДИКАТОРЫ СПРАВА (КАК В WORDFORMATION) */}
          <div className="wordformation-indicators">
            <div className={`indicator listen ${isListening ? 'active' : ''}`}>
              <span className="indicator-icon">🎧</span>
            </div>
            <div className={`indicator speak ${isSpeaking ? 'active' : ''}`}>
              <span className="indicator-icon">🗣️</span>
            </div>
          </div>

        </div>
      </div>
    </ModuleTemplate>
  );
};

export default Generalization;